import { type SchemaElement, type SchemaTree } from "hyparquet";

/**
 * Recursively flattens a SchemaTree into a flat array of SchemaElements
 * in pre-order traversal (parent followed by all descendants).
 */
function flattenSubtree(tree: SchemaTree, result: SchemaElement[]): void {
  result.push(tree.element);
  for (const child of tree.children) {
    flattenSubtree(child, result);
  }
}

/**
 * Build a schema tree from a flat schema array.
 * This is equivalent to hyparquet's internal schemaTree function.
 */
function buildSchemaTree(
  schema: SchemaElement[],
  rootIndex: number = 0,
  path: string[] = [],
): SchemaTree {
  const element = schema[rootIndex];
  const children: SchemaTree[] = [];
  let count = 1;

  if (element.num_children) {
    while (children.length < element.num_children) {
      const childElement = schema[rootIndex + count];
      const child = buildSchemaTree(schema, rootIndex + count, [
        ...path,
        childElement.name,
      ]);
      count += child.count;
      children.push(child);
    }
  }

  return { count, element, children, path };
}

/**
 * Looks up a top-level column by name in a Parquet schema and returns
 * its full subtree, including any nested children. Returns null if the
 * column is not found.
 */
export function findColumnSubtree(
  schema: SchemaElement[],
  columnName: string,
): SchemaTree | null {
  const root = buildSchemaTree(schema, 0, []);
  return root.children.find((c) => c.element.name === columnName) ?? null;
}

/**
 * Converts a column's schema subtree back into a flat array of
 * SchemaElements suitable for writing a single-column Parquet file.
 */
export function flattenColumnSubtree(columnTree: SchemaTree): SchemaElement[] {
  const result: SchemaElement[] = [];
  flattenSubtree(columnTree, result);
  return result;
}
